import Dialog from '@/components/Dialog';
import {
  type IndividualUserInviteSuccessResponse,
  type IndividualUserInviteErrorResponse,
} from '../types';
import { FormattedMessage } from 'react-intl';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import SharingErrorList from './SharingErrorList';

type SharingErrorDialogProps = {
  open: boolean;
  onClose: () => void;
  successfulUserInvitations:
    [string, IndividualUserInviteSuccessResponse][] | null;
  failedUserInvitations: [string, IndividualUserInviteErrorResponse][] | null;
};

type ErrorDialogVariant =
  | 'null_state'
  | 'all_failed_multiple'
  | 'all_failed_one'
  | 'some_failed'
  | 'one_failed_of_multiple';

const determineErrorDialogVariant = (
  successfulUserInvitations:
    [string, IndividualUserInviteSuccessResponse][] | null,
  failedUserInvitations: [string, IndividualUserInviteErrorResponse][] | null,
): ErrorDialogVariant => {
  if (successfulUserInvitations === null || failedUserInvitations === null) {
    return 'null_state';
  }

  if (
    successfulUserInvitations.length === 0 &&
    failedUserInvitations.length === 1
  ) {
    return 'all_failed_one';
  }

  if (successfulUserInvitations.length === 0) {
    return 'all_failed_multiple';
  }

  if (failedUserInvitations.length === 1) {
    return 'one_failed_of_multiple';
  }

  return 'some_failed';
};

const SharingErrorDialog = ({
  open,
  onClose,
  successfulUserInvitations,
  failedUserInvitations,
}: SharingErrorDialogProps) => {
  const variant = determineErrorDialogVariant(
    successfulUserInvitations,
    failedUserInvitations,
  );

  return (
    <Dialog open={open} onClose={onClose} dangerous>
      {variant !== 'null_state' && (
        <>
          <Dialog.Title>
            {(variant === 'all_failed_multiple' ||
              variant === 'all_failed_one') && (
              <FormattedMessageWithChallengeNaming id="portfolio.challenge.sharing.error_dialog.title.all_failed" />
            )}
            {variant === 'one_failed_of_multiple' && (
              <FormattedMessageWithChallengeNaming
                id="portfolio.challenge.sharing.error_dialog.title.one_failed_of_multiple"
                values={{
                  inviteeEmail: failedUserInvitations![0][0],
                }}
              />
            )}
            {variant === 'some_failed' && (
              <FormattedMessageWithChallengeNaming id="portfolio.challenge.sharing.error_dialog.title.some_failed" />
            )}
          </Dialog.Title>
          <Dialog.Content>
            <Dialog.ContentText>
              {variant === 'all_failed_multiple' && (
                <FormattedMessageWithChallengeNaming id="portfolio.challenge.sharing.error_dialog.text.all_failed_multiple" />
              )}
              {(variant === 'all_failed_one' ||
                variant === 'one_failed_of_multiple') && (
                <FormattedMessageWithChallengeNaming
                  id={`portfolio.challenge.sharing.error_dialog.text.one_failed.${failedUserInvitations![0][1].challengeSharingResult.toLowerCase()}`}
                  values={{
                    inviteeEmail: failedUserInvitations![0][0],
                  }}
                  hasHtml
                />
              )}
              {variant === 'some_failed' && (
                <FormattedMessageWithChallengeNaming id="portfolio.challenge.sharing.error_dialog.text.some_failed" />
              )}
            </Dialog.ContentText>
            {(variant === 'all_failed_multiple' ||
              variant === 'some_failed') && (
              <SharingErrorList
                failedUserInvitations={failedUserInvitations!}
              />
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Dialog.Actions.DeleteButton onClick={onClose}>
              <FormattedMessage id="dialogs.actions.ok" />
            </Dialog.Actions.DeleteButton>
          </Dialog.Actions>
        </>
      )}
    </Dialog>
  );
};

export default SharingErrorDialog;
